import { BigNum, PERCENTAGE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import React from 'react';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import use24hMarketDataFromStore from 'src/hooks/use24hMarketDataFromStore';
import { cj } from 'src/utils/classNames';
import Tag from 'src/components/primitives/Tag/Tag';
import { PRESETS } from 'src/utils/formatNumber/presets';

interface Props {
	marketId: MarketId | undefined;
	markPrice: BigNum;
	className?: string;
	withBackground?: boolean;
}

const PriceChangePercentDisplay_ = ({
	marketId,
	markPrice,
	className,
	withBackground,
}: Props) => {
	const dataForMarket = use24hMarketDataFromStore({
		targetMarketType: marketId?.marketType ?? 'perp',
		targetMarketIndex: marketId?.marketIndex ?? 0,
	})[0];

	// No record yet means the 24h fetch is still in flight. A record that
	// carries no change means the market has no 24h window to compare against,
	// which is permanent until it has been listed for a day.
	if (!marketId || !dataForMarket) {
		return <Skeleton />;
	}

	const pricePercentChange = (() => {
		const { price24hAgo, pricePercentChange } = dataForMarket;

		if (
			Number.isFinite(price24hAgo) &&
			price24hAgo !== 0 &&
			markPrice &&
			!markPrice.eqZero()
		) {
			return 100 * ((markPrice.toNum() - price24hAgo) / price24hAgo);
		}

		return Number.isFinite(pricePercentChange) ? pricePercentChange : undefined;
	})();

	if (pricePercentChange === undefined) {
		return (
			<Tag className={className}>
				<span className="text-text-tertiary">--</span>
			</Tag>
		);
	}

	const backgroundClassName = (() => {
		if (!withBackground) {
			return;
		}

		if (pricePercentChange > 0) {
			return 'bg-positive-green-secondary-bg';
		}

		return 'bg-negative-red-secondary-bg';
	})();

	return (
		<Tag className={cj(backgroundClassName, className)}>
			<NumberDisplay
				value={BigNum.fromPrint(
					pricePercentChange.toString(),
					PERCENTAGE_PRECISION_EXP
				)}
				options={PRESETS.percentSignedTruncate}
				signDependentColor
			/>
		</Tag>
	);
};

function Skeleton() {
	return (
		<SkeletonValuePlaceholder
			className="my-px w-10 xs:h-[11px] sm:h-[20px]"
			loading
		/>
	);
}

export const PriceChangePercentDisplay = React.memo(PriceChangePercentDisplay_);
